import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { buttonClass } from "@/components/buttonStyles";
import { PlaceholderPanel } from "@/base/parts";
import { SendPrequalificationDialog } from "@/components/SendPrequalificationDialog";
import { Pill } from "@/components/bond-submission/SubmissionShell";
import type { AccountPreapproval } from "@/lib/brokerAccounts";
import {
  getOrderIndicationLetterPdf,
  type BondSubmission,
} from "@/lib/brokerBonds";
import type { CapacityProject } from "@/lib/capacityRequest";
import { formatBondDate, formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { programFigures, programInfo } from "@/lib/programEnrollment";
import {
  advanceConnectionLine,
  countedSubmissions,
  programFit,
  programUtilization,
  shownProgram,
  type CountedRow,
  type ProgramFitView,
  type ProgramUtilizationView,
} from "@/lib/submissionProgram";

// BRK-S416, BRK-S502 — the Submission's Surety Program tab, as the October
// wireframe draws it: a "Surety Program" box (Send pre-qualification and
// Print indication letter in its header; CURRENT PROGRAM ENROLLMENT, the
// program, its dates and figures, and "Aggregate utilized" with THIS bond
// striped while it is not yet held); "This submission against the program"
// with its verdict pill and the submissions counted against it ("· this
// one"); and, on Signal Express only, the Signal Advance (Recommended) pitch.
// It renders `src/lib/submissionProgram.ts`'s reads and decides nothing (#7).
//
// BRK-S450: Print indication letter downloads THIS ORDER's letter
// (`getOrderIndicationLetterPdf`, PMS-S912), offered only while the
// order view's `indicationLetterAvailable` says so; the account page keeps
// the account's letter.
//
// Mobile-first: one column; the counted-submissions table stacks each row as
// a card at 375 and is a table from `sm:`.

/** The page's account read: still loading, failed (null), or the program. */
export type TabProgram = AccountPreapproval | null | "loading";

const money = formatPreapprovalMoney;
const NAMES = { express: "Signal Express", advance: "Signal Advance" } as const;

/** What to call the letter if PMS names none — never the order id. */
const LETTER_FALLBACK = "indication-letter.pdf";

export function SubmissionProgramTab({
  order,
  program,
  capacityProjects,
}: {
  order: BondSubmission;
  program: TabProgram;
  /** The account's capacity-projects read; null when unread or failed. */
  capacityProjects: readonly CapacityProject[] | null;
}) {
  const [prequalOpen, setPrequalOpen] = useState(false);
  const account = program === "loading" ? null : program;
  // BRK-S457 — the account's approved program unless the order carries its
  // own approval; see `shownProgram` for the rule.
  const read = shownProgram(order.preapproval, program);
  const shown = read === "loading" ? null : read;
  const enrolled = shown?.status === "approved";
  const isDraft = order.status === "draft";
  const noProgram = order.program === null;
  const capacity = account?.capacity ?? null;
  const fit = isDraft
    ? null
    : programFit({ order, capacity, projects: capacityProjects });
  const utilization = programUtilization({
    order,
    capacity,
    projects: capacityProjects,
  });

  const header = (
    <>
      {enrolled && !noProgram && (
        <button
          type="button"
          onClick={() => setPrequalOpen(true)}
          className={buttonClass({ variant: "secondary", size: "sm" })}
        >
          Send pre-qualification
        </button>
      )}
      {order.indicationLetterAvailable && (
        <PrintOrderLetter orderId={order.id} />
      )}
    </>
  );

  return (
    <>
      <CollapsibleBox title="Surety Program" actions={header}>
        {noProgram ? (
          <NoProgramBox accountId={order.accountId} />
        ) : shown && enrolled ? (
          <EnrollmentBlock preapproval={shown} utilization={utilization} />
        ) : (
          <PlaceholderPanel>
            {read === "loading"
              ? "Loading the insured’s program…"
              : "We couldn’t load the insured’s program. It’s on the account page."}
          </PlaceholderPanel>
        )}
      </CollapsibleBox>
      {enrolled && !noProgram && (
        <SendPrequalificationDialog
          accountId={order.accountId}
          open={prequalOpen}
          onClose={() => setPrequalOpen(false)}
        />
      )}

      {fit && (
        <CollapsibleBox
          title="This submission against the program"
          actions={fit.pill ? <Pill pill={fit.pill} /> : undefined}
        >
          <FitBlock
            fit={fit}
            rows={countedSubmissions(order, capacityProjects)}
          />
        </CollapsibleBox>
      )}

      {shown?.status === "approved" &&
        shown.programType === "express" &&
        !noProgram && (
          <CollapsibleBox title="Signal Advance (Recommended)">
            <AdvancePitch order={order} preapproval={shown} />
          </CollapsibleBox>
        )}
    </>
  );
}

/** Print indication letter — THIS order's (BRK-S450). The server names the
 *  file; a failure says so in its words, inline; "Preparing…" only while the
 *  request is out, and nothing claims success without the 2xx. */
function PrintOrderLetter({ orderId }: { orderId: string }) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function print() {
    setBusy(true);
    setError(null);
    try {
      saveFile(
        await getOrderIndicationLetterPdf(authedBlob, orderId),
        LETTER_FALLBACK,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex flex-col gap-1">
      <button
        type="button"
        onClick={() => void print()}
        disabled={busy}
        className={buttonClass({ variant: "secondary", size: "sm" })}
      >
        {busy ? "Preparing…" : "Print indication letter"}
      </button>
      {error && (
        <span role="alert" className="text-xs text-brk-danger">
          {error}
        </span>
      )}
    </span>
  );
}

/** CURRENT PROGRAM ENROLLMENT: the program, its pill, its dates, its three
 *  figures and the aggregate utilized bar. */
function EnrollmentBlock({
  preapproval,
  utilization,
}: {
  preapproval: AccountPreapproval;
  utilization: ProgramUtilizationView | null;
}) {
  const type = preapproval.programType;
  const name = type === null ? "Surety program" : NAMES[type];
  const info = type === null ? null : programInfo(type, preapproval);
  const dates = [
    preapproval.evaluatedAt
      ? `Program approved ${formatBondDate(preapproval.evaluatedAt)}`
      : null,
    preapproval.expiresAt
      ? `Expires ${formatBondDate(preapproval.expiresAt)}`
      : null,
    info ? `${info.tag} · ${info.points[0] ?? ""}`.replace(/ · $/, "") : null,
  ].filter((p): p is string => p !== null);
  return (
    <section
      aria-label="Current program enrollment"
      className="flex min-w-0 flex-col gap-4"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-brk-dim">
        Current program enrollment
      </p>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h3 className="text-heading text-brk-text">{name}</h3>
        <Pill pill={{ label: "Approved", tone: "done" }} />
      </div>
      {dates.length > 0 && (
        <p className="break-words text-sm text-brk-dim">{dates.join(" · ")}</p>
      )}
      {preapproval.terms && (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {programFigures(preapproval.terms).map((f) => (
            <FitLine key={f.label} label={f.label} value={f.value} />
          ))}
        </dl>
      )}
      {utilization && <UtilizationBar view={utilization} />}
    </section>
  );
}

/** "Aggregate utilized": held solid, THIS bond striped while not yet held. */
function UtilizationBar({ view }: { view: ProgramUtilizationView }) {
  const pct = (n: number) => Math.min(100, Math.max(0, (n / view.limit) * 100));
  const usedLine = [
    `${money(view.used)} of ${money(view.limit)} used`,
    view.projected !== null
      ? `${money(view.projected)} more once this bond is issued (striped)`
      : null,
  ].filter((p): p is string => p !== null);
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-sm font-medium text-brk-text">
        Aggregate utilized
      </span>
      <div
        role="progressbar"
        aria-label="Aggregate utilized"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct(view.used))}
        aria-valuetext={usedLine.join(" · ")}
        className="flex h-2 w-full overflow-hidden rounded-full bg-brk-border"
      >
        <div
          className="h-full bg-brk-accent"
          style={{ width: `${String(pct(view.used))}%` }}
        />
        {view.projected !== null && (
          <div
            data-projected
            className="h-full"
            style={{
              width: `${String(pct(view.projected))}%`,
              backgroundImage:
                "repeating-linear-gradient(45deg, rgb(var(--brk-accent) / 0.6) 0 4px, transparent 4px 8px)",
            }}
          />
        )}
      </div>
      <p className="flex min-w-0 flex-wrap justify-between gap-x-3 text-xs text-brk-dim">
        <span className="break-words">{usedLine.join(" · ")}</span>
        <span>{money(view.available)} available</span>
      </p>
    </div>
  );
}

/** The wireframe's no-program box, in D2's advisory wording: readiness is
 *  visibility, never a gate. */
function NoProgramBox({ accountId }: { accountId: string }) {
  return (
    <section
      aria-label="No surety program"
      className="flex min-w-0 flex-col items-start gap-3"
    >
      <Pill pill={{ label: "No program", tone: "danger" }} />
      <p className="text-sm text-brk-text">
        No surety program. This submission needs a program before a bond can
        issue.
      </p>
      <Link
        to={`/surety/accounts/${encodeURIComponent(accountId)}?tab=program`}
        className={buttonClass()}
      >
        Enroll on the account page
      </Link>
    </section>
  );
}

function FitLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption font-medium uppercase tracking-wide text-brk-dim">
        {label}
      </dt>
      <dd className="break-words text-subheading text-brk-text">{value}</dd>
    </div>
  );
}

function FitBlock({ fit, rows }: { fit: ProgramFitView; rows: CountedRow[] }) {
  const tableId = useId();
  return (
    <section
      aria-label="This submission against the program"
      className="flex min-w-0 flex-col gap-4"
    >
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FitLine label="Contract price" value={fit.contract ?? "Not on file"} />
        <FitLine
          label="Share of single bond limit"
          value={fit.singleShare ?? "Not available"}
        />
        <FitLine
          label={fit.premium.label}
          value={fit.premium.value ?? "Not estimated yet"}
        />
        {fit.aggregate && (
          <FitLine label={fit.aggregate.label} value={fit.aggregate.value} />
        )}
      </dl>
      {rows.length > 0 && (
        <table
          aria-labelledby={tableId}
          className="block w-full min-w-0 text-sm sm:table"
        >
          <caption id={tableId} className="sr-only">
            Submissions counted against the program
          </caption>
          <thead className="hidden sm:table-header-group">
            <tr className="border-b border-brk-border text-left text-caption uppercase tracking-wide text-brk-dim">
              <th scope="col" className="py-2 pr-3 font-medium">
                Submission
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Status
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Contract size
              </th>
              <th scope="col" className="py-2 font-medium">
                Counts toward
              </th>
            </tr>
          </thead>
          <tbody className="flex flex-col gap-2 sm:table-row-group">
            {rows.map((r) => (
              <CountedRowView key={r.orderId} row={r} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** One counted submission: a card at 375 (each cell labelled), a table row
 *  from `sm:`. This submission is highlighted and `aria-current`. */
function CountedRowView({ row }: { row: CountedRow }) {
  const cell = "flex min-w-0 gap-2 sm:table-cell sm:py-2 sm:pr-3";
  const label = "text-brk-dim sm:hidden";
  return (
    <tr
      aria-current={row.current ? "true" : undefined}
      className={`flex flex-col gap-1 rounded-lg border border-brk-border p-3 sm:table-row sm:rounded-none sm:border-0 sm:border-b sm:p-0 ${
        row.current ? "bg-brk-accent/10" : ""
      }`}
    >
      <td className={cell}>
        {/* Its own flex row: from `sm:` the cell is a table cell, where the
            cell's gap does nothing and the number ran into the project's
            name (BRK-S429). */}
        <span className="flex min-w-0 flex-wrap gap-x-2">
          {row.current ? (
            <span className="font-medium text-brk-text">{row.number}</span>
          ) : (
            <Link
              to={row.href}
              className="font-medium text-brk-accent hover:underline"
            >
              {row.number}
            </Link>
          )}
          {row.current && <span className="text-brk-dim">· this one</span>}
          {row.projectName && (
            <span className="break-words text-brk-dim">{row.projectName}</span>
          )}
        </span>
      </td>
      <td className={cell}>
        <span className={label}>Status</span>
        <Pill pill={{ label: row.status, tone: "neutral" }} />
      </td>
      <td className={`${cell} tabular-nums`}>
        <span className={label}>Contract size</span>
        {row.amount ?? "—"}
      </td>
      <td className={cell}>
        <span className={label}>Counts toward</span>
        {row.counts}
      </td>
    </tr>
  );
}

/** The Signal Advance pitch, for an Express program only: what the program
 *  is (the info popover's own words, no marketing figure), the connection
 *  line from the order, and the way to the account's application. */
function AdvancePitch({
  order,
  preapproval,
}: {
  order: BondSubmission;
  preapproval: AccountPreapproval;
}) {
  const info = programInfo("advance", preapproval);
  return (
    <section
      aria-label={info.title}
      className="flex min-w-0 flex-col items-start gap-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-subheading text-brk-text">{info.tag}</p>
        <Pill pill={{ label: "Recommended", tone: "active" }} />
      </div>
      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-brk-text">
        {info.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      <p className="break-words text-sm text-brk-dim">
        {advanceConnectionLine(order).join(" · ")}
      </p>
      <Link
        to={`/surety/accounts/${encodeURIComponent(order.accountId)}?tab=program#advance-app`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass({ variant: "secondary" })}
      >
        Enroll on the account page
        <ExternalLink aria-hidden="true" className="h-4 w-4" />
      </Link>
    </section>
  );
}
